import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { API, apiError } from "@/lib/api";
import { HOLY_PHOTOS, holySrc } from "@/lib/holyPhotos";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, X, Loader2, Images } from "lucide-react";

const MAX = 10;
export const photoSrc = (p) => (p.lib ? holySrc(p.lib) : `${API}/public/package-photos/${p.id}`);

export const HolyPicker = ({ open, onOpenChange, onPick, selected }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#05261D] border-gold/20 max-w-2xl">
      <DialogHeader>
        <DialogTitle className="font-serif text-gold">Makkah & Madinah Photos</DialogTitle>
        <DialogDescription>Tap a photo to use it.</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-[60vh] overflow-y-auto">
        {HOLY_PHOTOS.map((h) => (
          <button key={h.key} type="button" data-testid={`holy-pick-${h.key}`} onClick={() => onPick(h.key)}
            className={`relative rounded-lg overflow-hidden border-2 transition-colors ${selected === h.key ? "border-gold" : "border-transparent hover:border-gold/50"}`}>
            <img src={h.src} alt={h.label} className="w-full aspect-square object-cover" />
            <span className="absolute bottom-0 inset-x-0 bg-black/60 text-[9px] text-white px-1 py-0.5 truncate">{h.label}</span>
          </button>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

export const PackagePhotos = ({ pkgId }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    const r = await fetch(`${API}/public/package-photos`).then((x) => x.json()).catch(() => ({}));
    setItems(r[pkgId] || []);
  }, [pkgId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    const list = Array.from(files || []).slice(0, MAX - items.length);
    if (!list.length) { toast.error(`Maximum ${MAX} photos per package`); return; }
    setBusy(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("photo", f);
        await api.post(`/packages/${pkgId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${list.length} photo(s) added`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  const addLib = async (key) => {
    try { await api.post(`/packages/${pkgId}/photos/library`, { key }); setLibOpen(false); load(); toast.success("Photo added"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/packages/photos/${id}`); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="col-span-12 flex flex-wrap items-center gap-2 pt-1" data-testid={`pkg-photos-${pkgId}`}>
      {items.map((p) => (
        <div key={p.id} className="relative">
          <img src={photoSrc(p)} alt="package" className="w-14 h-14 rounded-md object-cover border border-gold/30" />
          <button type="button" data-testid={`pkg-photo-del-${p.id}`} onClick={() => remove(p.id)}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center">
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      {items.length < MAX && (
        <>
          <button type="button" data-testid={`pkg-photo-add-${pkgId}`} onClick={() => ref.current?.click()} disabled={busy}
            className="w-14 h-14 rounded-md border border-dashed border-gold/50 text-gold flex flex-col items-center justify-center hover:bg-gold/10 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            <span className="text-[8px] mt-0.5">Upload</span>
          </button>
          <button type="button" data-testid={`pkg-photo-lib-${pkgId}`} onClick={() => setLibOpen(true)}
            className="w-14 h-14 rounded-md border border-dashed border-gold/50 text-gold flex flex-col items-center justify-center hover:bg-gold/10 transition-colors">
            <Images className="w-4 h-4" />
            <span className="text-[8px] mt-0.5">Library</span>
          </button>
        </>
      )}
      <span className="text-[10px] text-muted-foreground">{items.length}/{MAX} photos</span>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
      <HolyPicker open={libOpen} onOpenChange={setLibOpen} onPick={addLib} />
    </div>
  );
};

export const PackageSlider = ({ items }) => {
  const [i, setI] = useState(0);
  if (!items?.length) return null;
  return (
    <div className="relative h-44 overflow-hidden bg-black/30" data-testid="pkg-slider">
      <img src={photoSrc(items[i])} alt="package" className="w-full h-full object-cover" />
      {items.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
          {items.map((p, x) => (
            <button key={p.id} type="button" aria-label={`photo ${x + 1}`} onClick={() => setI(x)}
              className={`h-1.5 rounded-full transition-all ${x === i ? "w-5 bg-gold" : "w-1.5 bg-white/60"}`} />
          ))}
        </div>
      )}
    </div>
  );
};
